import { QueryHookOptions, QueryResult } from '@apollo/client';
import { useMemo } from 'react';

import Item from '../components/Item';
import { TypeAheadFieldValues, MenuItemsComponentProps, TypeAheadItem } from '../types';

interface GetApolloMenuItemsComponentOptions<QueryData, Value extends TypeAheadFieldValues> {
  useQuery(
    baseOptions: QueryHookOptions<QueryData, { search: string }>
  ): QueryResult<QueryData, { search: string }>;
  formatItems(data?: QueryData): Array<TypeAheadItem<Value>>;
}

const getApolloMenuItemsComponent =
  <QueryData, Value extends TypeAheadFieldValues>({
    useQuery,
    formatItems,
  }: GetApolloMenuItemsComponentOptions<QueryData, Value>) =>
  ({
    inputValue,
    getItemProps,
    selectedItem,
    highlightedIndex,
  }: MenuItemsComponentProps<Value>) => {
    const { data, loading } = useQuery({
      variables: {
        search: inputValue ?? '',
      },
      skip: !inputValue || inputValue.length < 3,
    });

    const items = useMemo(() => formatItems(data), [data]);

    if (loading) {
      return <Item label="Searching..." disabled />;
    }

    if (!inputValue || inputValue.length < 3) {
      return (
        <Item label={`Please enter ${3 - (inputValue ?? '').length} or more characters`} disabled />
      );
    }

    if (!items.length) {
      return <Item label="No results found" disabled />;
    }

    return (
      <>
        {items.map((item, index) => (
          <Item
            key={item.value}
            {...item}
            selected={selectedItem?.value === item.value}
            highlighted={highlightedIndex === index}
            {...getItemProps({
              item,
              index,
            })}
          />
        ))}
      </>
    );
  };

export default getApolloMenuItemsComponent;
